import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { CallbackOutcome, UserClaims } from "./cognito";
import { getAuthClient } from "./config";
import { CONSOLE_ROOT } from "./returnPath";
import { type AuthState, AuthCtx } from "./context";

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const client = getAuthClient();
  const [claims, setClaims] = useState<UserClaims | undefined>(() =>
    client?.getClaims(),
  );

  const login = useCallback(
    (returnTo?: string) => {
      void client?.login(returnTo);
    },
    [client],
  );

  const logout = useCallback(() => {
    client?.logout();
  }, [client]);

  /**
   * Handle a hosted-UI redirect and report the outcome.
   *
   * The outcome is returned rather than swallowed: a callback that fails is
   * something the person needs told, and silently landing them back on the
   * sign-in page reads as a bug. Without a configured client there is no OAuth
   * round trip at all, so the URL is by definition not a callback.
   */
  const completeLogin = useCallback(async (): Promise<CallbackOutcome> => {
    if (!client) return { status: "not-a-callback" };
    const outcome = await client.handleRedirectCallback();
    if (outcome.status === "signed-in") setClaims(outcome.claims);
    return outcome;
  }, [client]);

  const consumeReturnPath = useCallback(
    () => client?.consumeReturnPath() ?? CONSOLE_ROOT,
    [client],
  );

  const consumeSignedOut = useCallback(
    () => client?.consumeSignedOutFlag() ?? false,
    [client],
  );

  const value = useMemo<AuthState>(
    () => ({
      configured: client !== undefined,
      authenticated: client?.isAuthenticated() ?? false,
      sessionExpired: client?.hasExpiredSession() ?? false,
      claims,
      login,
      logout,
      completeLogin,
      consumeReturnPath,
      consumeSignedOut,
    }),
    [
      client,
      claims,
      login,
      logout,
      completeLogin,
      consumeReturnPath,
      consumeSignedOut,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
